// Session inactivity plumbing shared by AuthContext, SessionGuard and autosave hooks.
// Only genuine user input calls markActivity(); background API calls never do.
import { api } from "./api";

export const ACTIVITY_KEY = "vien_last_activity"; // epoch ms only — never PHI
const CHANNEL = "vien-session";
const HEARTBEAT_MS = 60000;

const bc = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(CHANNEL) : null;
let lastHeartbeat = 0;

export const readActivity = () => Number(localStorage.getItem(ACTIVITY_KEY) || 0);
export function markActivity(now = Date.now()) {
    localStorage.setItem(ACTIVITY_KEY, String(now)); // storage event keeps other tabs in sync
    if (now - lastHeartbeat >= HEARTBEAT_MS) { lastHeartbeat = now; api.post("/auth/activity").catch(() => {}); }
}
export function forceHeartbeat() { lastHeartbeat = 0; markActivity(); }

export const broadcast = (msg) => bc?.postMessage(msg);
export function onBroadcast(fn) { if (!bc) return () => {}; const h = (e) => fn(e.data); bc.addEventListener("message", h); return () => bc.removeEventListener("message", h); }

// Autosave hooks register a flush so pending clinical edits reach the server before an idle logout.
const flushers = new Set();
export function registerBeforeLogout(fn) { flushers.add(fn); return () => flushers.delete(fn); }
export async function runBeforeLogout(timeoutMs = 4000) {
    const timeout = new Promise((res) => setTimeout(() => res("timeout"), timeoutMs));
    const results = await Promise.race([Promise.allSettled([...flushers].map((f) => f())), timeout]);
    if (results === "timeout") return { ok: false, timedOut: true };
    return { ok: results.every((r) => r.status === "fulfilled" && r.value !== false), timedOut: false };
}

// In-memory only (survives the SPA redirect to sign-in, never written to disk/storage → no PHI at rest).
// Drafts are owned by the user who typed them; a different user signing in on the same tab never sees them.
const drafts = new Map();
let draftOwner = null;
export function setDraftOwner(userId) {
    if (draftOwner && userId && draftOwner !== userId) drafts.clear();
    if (userId) { draftOwner = userId; sessionStorage.setItem("vien_last_uid", userId); }
}
export const clearDrafts = () => { drafts.clear(); sessionStorage.removeItem("vien_last_uid"); };
export const keepDraft = (key, value) => drafts.set(key, { value, at: Date.now(), owner: draftOwner });
export const takeDraft = (key) => { const d = drafts.get(key); drafts.delete(key); return d && d.owner && d.owner === draftOwner ? d : null; };
export const hasDraft = (key) => drafts.has(key) && drafts.get(key).owner === draftOwner;


export function safeReturnTo(raw) {
    if (!raw || typeof raw !== "string") return null;
    if (!raw.startsWith("/") || raw.startsWith("//") || raw.includes("://")) return null;
    if (/^\/(login|signin|register|platform-access|change-password|forgot-password|activate|legal)/.test(raw)) return null;
    return raw;
}
